import { useLingui } from '@lingui/react/macro'

import { EPayButtonStatus } from '../type'

import { usePayBase } from './payBase'
import { useGetPaymentLink } from './getPaymentLink'
import { useSubCreate } from './subCreate'
import { useSubscriptionChange } from './subscriptionChange'

import type { EPaidProductType, EProductType, TProduct } from '../type'
import type { $Enums } from '@prisma/client'
import type { TUsePayStep } from './payStep'

import { useContactUs } from '@/hooks/contactUs'


import { useMemo } from 'react';
export interface ICommonButtonProps {
  type: EProductType | EPaidProductType
  productPriceCredits: TProduct
  buyText?: React.ReactNode
  onUpgradeSuccess?: () => void
  appName: $Enums.SubLogAppName
  payStepInfo: TUsePayStep
}

export function usePayButton(props: ICommonButtonProps) {
  const { t } = useLingui()
  const {
    productPriceCredits,
    buyText = t`Subscribe`,
    onUpgradeSuccess,
    appName,
    payStepInfo,
  } = props

  const {
    currentPlan,
    canSubscription,
    isSignIn,
    loading: baseLoading,
    isAbnormalUser,
    userId,
    plan,
    usage,
  } = usePayBase({ productPriceCredits })

  const {
    onChangePayStep,
    onPaypalClick,
    paymentPlatformInfo,
    paymentLoading,
    getPayInsiderLoading,
  } = payStepInfo
  const {
    firstPaymentPlatform,
    firstPaymentPlatformIsFastspring,
    firstPaymentPlatformIsPaypal,
    isInitialLoading: platformLoading,
  } = paymentPlatformInfo
  const platformName = firstPaymentPlatform?.platformName

  const {
    subscriptionId,
    isSub,
    customerId,
    hasActivePlan,
    creditsPlan,
    planPlatformInfoEnable,
    planPlatformName,
    hasPlan,
    expirePayment,
  } = plan

  const isMonth = productPriceCredits?.duration === 1
  const hasReusableWebPaymentMethod =
    planPlatformInfoEnable && !!planPlatformName

  const planStatus = currentPlan?.status ?? ''

  /**
   * @description: 状态不同 调用 不同接口
   */
  const hasPlanCanChange = useMemo(() => {
    return (
      planStatus === 'active' ||
      planStatus === 'trialing' ||
      // planStatus === 'incomplete_expired' ||
      planStatus === 'past_due' ||
      // planStatus === 'incomplete' ||
      planStatus === 'canceled'
    )
    // planStatus === 'unpaid'
  }, [planStatus])

  /**
   * @description: 是否是 用户当前套餐
   */
  const isUserCurrentPlan = useMemo(() => {
    const currentProduct = creditsPlan?.productsSub

    if (!currentProduct || !productPriceCredits) {
      return false
    }

    // 先按精确的 priceId 命中当前套餐；只有老数据缺少 priceId 时才退回到 upgradeWeight。
    if (currentProduct.priceId && productPriceCredits.priceId) {
      return currentProduct.priceId === productPriceCredits.priceId
    }

    return currentProduct.upgradeWeight === productPriceCredits.upgradeWeight
  }, [creditsPlan, productPriceCredits])

  const { status, text } = useMemo(() => {
    // 后台管理赋予的套餐
    if (isAbnormalUser) {
      return {
        text: t`Unavailable`,
        status: EPayButtonStatus.disabled,
      }
    }

    if (!planPlatformInfoEnable) {
      return {
        text: <>{buyText}</>,
        status: EPayButtonStatus.start,
      }
    }

    // 当前套餐 文案
    if (isUserCurrentPlan && hasActivePlan) {
      return {
        text: <>{t`Current Plan`}</>,
        status: EPayButtonStatus.current,
      }
    }

    // 免费套餐 文案
    if (!hasPlan) {
      return {
        text: <>{buyText}</>,
        status: EPayButtonStatus.start,
      }
    }

    if (canSubscription) {
      // 升级套餐（改变） 文案
      return {
        text: <>{t`Subscribe`}</>,
        status: EPayButtonStatus.upgrade,
      }
    } else {
      // 不能升级
      return {
        text: <>{t`Unavailable`}</>,
        status: EPayButtonStatus.disabled,
      }
    }
  }, [
    isAbnormalUser,
    isUserCurrentPlan,
    hasActivePlan,
    hasPlan,
    canSubscription,
    buyText,
    planPlatformInfoEnable,
    t,
  ])

  const priceId = useMemo(() => {
    return productPriceCredits?.priceId || ''
  }, [productPriceCredits])

  const { isLoading: getPaymentLinkLoading } = useGetPaymentLink({ appName })

  const { subCreate, subCreateLoading } = useSubCreate({})

  const { subscriptionChange, isLoading: upgradeLoading } =
    useSubscriptionChange({
      onUpgradeSuccess,
    })

  const buttonLoading = useMemo(() => {
    return getPaymentLinkLoading || upgradeLoading || subCreateLoading
  }, [getPaymentLinkLoading, subCreateLoading, upgradeLoading])

  const loading = useMemo(() => {
    return (
      buttonLoading ||
      baseLoading ||
      platformLoading ||
      paymentLoading ||
      getPayInsiderLoading
    )
  }, [
    buttonLoading,
    baseLoading,
    platformLoading,
    paymentLoading,
    getPayInsiderLoading,
  ])

  const currentHref = typeof window === 'undefined' ? '' : window.location.href

  const { shouldShowContactUsModal } = useContactUs()

  const onClick = async () => {
    // 基础信息加载中，不允许点击
    if (loading) {
      return
    }

    const basePaidProductInfo = {
      appName,
      pricesId: [priceId],
      userId,
      productPriceList: [productPriceCredits],
      platformName,
    }

    const createAction = 'create' as const
    const upgradeAction = 'upgrade' as const

    const paidProduct = {
      ...basePaidProductInfo,
      cancelUrl: currentHref,
      action: createAction,
    }

    const handlePaymentProcess = async () => {
      if (!isSignIn) {
        onChangePayStep({
          paidProduct,
        })
        return
      }

      // 当前套餐 点击
      if (isUserCurrentPlan && hasActivePlan && planPlatformInfoEnable) {
        return
      }

      if (hasPlanCanChange) {
        // 升级套餐
        if (canSubscription) {
          if (firstPaymentPlatformIsPaypal) {
            onPaypalClick({
              paidProduct: { ...paidProduct, action: upgradeAction },
            })
            return
          }
          // Apple / Google 等 app 内购在 web 侧没有可复用的支付方式时，
          // 需要改走绑卡支付流程，而不是直接调升级扣款。
          if (expirePayment || !hasReusableWebPaymentMethod) {
            onChangePayStep({
              paidProduct: { ...paidProduct, action: upgradeAction },
            })
          } else {
            const flag = shouldShowContactUsModal({ action: 'upgrade' })
            if (!flag) {
              await subscriptionChange({
                ...basePaidProductInfo,
                subscriptionId,
                usage,
              })
            }
          }
        } else {
          // 点击的套餐，不能 小于 当前套餐
        }
      } else {
        // 购买套餐
        if (firstPaymentPlatformIsPaypal) {
          onPaypalClick({ paidProduct })
          return
        }
        if (
          isSub &&
          !expirePayment &&
          !firstPaymentPlatformIsFastspring &&
          planPlatformInfoEnable
        ) {
          await subCreate({
            ...basePaidProductInfo,
            customerId,
          })
        } else {
          onChangePayStep({
            paidProduct,
          })
        }
      }
    }

    handlePaymentProcess()
  }

  const btnDisabled = useMemo(() => {
    // return true
    return [EPayButtonStatus.disabled, EPayButtonStatus.current].includes(
      status,
    )
  }, [status])

  return {
    status,
    text,
    onClick,
    upgradeLoading,
    buttonLoading,
    baseLoading,
    isMonth,
    isSub,
    btnDisabled,
  }
}
